import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { getLegalMetadata } from '../api/legal';
import PageMetadata from '../components/PageMetadata';
import PublicFooter from '../components/PublicFooter';
import PublicHeader from '../components/PublicHeader';
import { legalPolicies } from '../content/legalPolicies';

const routeKeys = {
  terms: 'terms',
  privacy: 'privacy',
  security: 'security',
  'facility-authority': 'facility_authority',
};

function formatPolicyDate(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return value;
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${value}T00:00:00Z`));
}

export default function LegalPolicy() {
  const { policy } = useParams();
  const key = routeKeys[policy];
  const content = legalPolicies[key];
  const [documents, setDocuments] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    getLegalMetadata()
      .then((response) => {
        const receivedDocuments = response?.data?.documents;
        if (!receivedDocuments || typeof receivedDocuments !== 'object') {
          throw new Error('Missing legal document metadata');
        }
        setDocuments(receivedDocuments);
      })
      .catch(() => setError('Legal document metadata is temporarily unavailable.'));
  }, []);

  if (!content) return <Navigate to="/" replace />;
  const metadata = documents?.[key];
  const isLoading = documents === null && !error;
  const metadataUnavailable = !isLoading && (!metadata || error);
  const title = metadata?.name || 'CounselVault Legal Policy';
  const path = metadata?.public_route || `/legal/${policy}`;

  return (
    <div className="cv-site">
      <PageMetadata title={`${title} | CounselVault`} description={content.description} path={path} />
      <PublicHeader />
      <main className="cv-legal-main">
        <header className="cv-legal-header">
          <span className="cv-kicker">CounselVault legal</span>
          <h1>{title}</h1>
          {metadataUnavailable && <div className="alert alert-warning" role="alert">Legal document metadata is temporarily unavailable. The policy content remains available below.</div>}
          <dl className="cv-policy-meta">
            <div><dt>Version</dt><dd>{isLoading ? 'Loading metadata…' : (metadata?.display_version || 'Unavailable')}</dd></div>
            <div><dt>Effective date</dt><dd>{isLoading ? 'Loading metadata…' : (formatPolicyDate(metadata?.effective_date) || 'Unavailable')}</dd></div>
            <div><dt>Last updated</dt><dd>{isLoading ? 'Loading metadata…' : (formatPolicyDate(metadata?.last_updated_date) || 'Unavailable')}</dd></div>
          </dl>
        </header>

        <nav className="cv-legal-nav" aria-label="Legal document navigation">
          <Link to="/legal/terms">Terms of Service</Link>
          <Link to="/legal/privacy">Privacy Policy</Link>
          <Link to="/legal/security">Security Policy</Link>
          <Link to="/legal/facility-authority">Facility Authority Agreement</Link>
        </nav>

        <article className="cv-legal-article">
          {content.sections.map((section) => (
            <section key={section.heading}>
              <h2>{section.heading}</h2>
              {section.paragraphs?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
              {section.list && <ul>{section.list.map((item) => <li key={item}>{item}</li>)}</ul>}
            </section>
          ))}
        </article>
      </main>
      <PublicFooter />
    </div>
  );
}
